import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ModalidadSeleccionService } from '../services/modalidad-seleccion.service';
import * as ModalidadSeleccionActions from './modalidad-seleccion.actions';
import * as ModalidadSeleccionSelectors from './modalidad-seleccion.selectors';

@Injectable()
export class ModalidadSeleccionEffects {
  private readonly actions$ = inject(Actions);
  private readonly modalidadSeleccionService = inject(ModalidadSeleccionService);
  private readonly store = inject(Store);

  // Load Modalidades Seleccion
  loadModalidadesSeleccion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModalidadSeleccionActions.loadModalidadesSeleccion),
      switchMap(() =>
        this.modalidadSeleccionService.getModalidadesSeleccion().pipe(
          map(modalidadesSeleccion =>
            ModalidadSeleccionActions.loadModalidadesSeleccionSuccess({ modalidadesSeleccion })
          ),
          catchError(error =>
            of(
              ModalidadSeleccionActions.loadModalidadesSeleccionFailure({
                error: error.message || 'Error al cargar las modalidades de selección'
              })
            )
          )
        )
      )
    )
  );

  // Create Modalidad Seleccion
  createModalidadSeleccion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModalidadSeleccionActions.createModalidadSeleccion),
      switchMap(({ modalidadSeleccion }) =>
        this.modalidadSeleccionService.createModalidadSeleccion(modalidadSeleccion).pipe(
          map(createdModalidadSeleccion =>
            ModalidadSeleccionActions.createModalidadSeleccionSuccess({
              modalidadSeleccion: createdModalidadSeleccion
            })
          ),
          catchError(error =>
            of(
              ModalidadSeleccionActions.createModalidadSeleccionFailure({
                error: error.message || 'Error al crear la modalidad de selección'
              })
            )
          )
        )
      )
    )
  );

  // Update Modalidad Seleccion
  updateModalidadSeleccion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModalidadSeleccionActions.updateModalidadSeleccion),
      switchMap(({ id, modalidadSeleccion }) =>
        this.modalidadSeleccionService.updateModalidadSeleccion(id, modalidadSeleccion).pipe(
          switchMap(() =>
            this.modalidadSeleccionService.getModalidadSeleccionById(id).pipe(
              map(updatedModalidadSeleccion =>
                ModalidadSeleccionActions.updateModalidadSeleccionSuccess({
                  modalidadSeleccion: updatedModalidadSeleccion
                })
              )
            )
          ),
          catchError(error =>
            of(
              ModalidadSeleccionActions.updateModalidadSeleccionFailure({
                error: error.message || 'Error al actualizar la modalidad de selección'
              })
            )
          )
        )
      )
    )
  );

  // Toggle Status
  toggleModalidadSeleccionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModalidadSeleccionActions.toggleModalidadSeleccionStatus),
      switchMap(({ id, isActive }) =>
        this.modalidadSeleccionService.toggleModalidadSeleccionStatus(id, isActive).pipe(
          switchMap(() =>
            this.modalidadSeleccionService.getModalidadSeleccionById(id).pipe(
              map(updatedModalidadSeleccion =>
                ModalidadSeleccionActions.toggleModalidadSeleccionStatusSuccess({
                  modalidadSeleccion: updatedModalidadSeleccion
                })
              )
            )
          ),
          catchError(error =>
            of(
              ModalidadSeleccionActions.toggleModalidadSeleccionStatusFailure({
                error: error.message || 'Error al cambiar el estado de la modalidad de selección'
              })
            )
          )
        )
      )
    )
  );

  // Search Modalidades Seleccion
  searchModalidadesSeleccion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ModalidadSeleccionActions.searchModalidadesSeleccion),
      withLatestFrom(this.store.select(ModalidadSeleccionSelectors.selectAllModalidadesSeleccion)),
      map(([{ query }, allModalidades]) => {
        if (!query || query.trim() === '') {
          return ModalidadSeleccionActions.searchModalidadesSeleccionSuccess({ results: [] });
        }

        const filteredResults = allModalidades.filter(
          ms =>
            ms.codigo.toLowerCase().includes(query.toLowerCase()) ||
            (ms.caracteristicas && ms.caracteristicas.toLowerCase().includes(query.toLowerCase()))
        );

        return ModalidadSeleccionActions.searchModalidadesSeleccionSuccess({
          results: filteredResults
        });
      })
    )
  );
}
